import { Controller } from "@hotwired/stimulus"

const CONFIGURED_ZOOM_STEPS = [1, 1.5, 2, 3, 4]
const FULL_LOAD_ERROR_MESSAGE = "Unable to load larger image."
const IMAGE_LOAD_ERROR_MESSAGE = "Unable to load image."
const SWIPE_THRESHOLD_PX = 50
const SWIPE_DIRECTION_LOCK_PX = 12
const PINCH_DISTANCE_EPSILON = 0.5

export default class extends Controller {
  static targets = [
    "overlay",
    "viewport",
    "stage",
    "image",
    "loading",
    "caption",
    "closeButton",
    "previousButton",
    "nextButton",
    "item"
  ]

  connect() {
    this.handleKeydown = this.handleKeydown.bind(this)
    this.handleResize = this.handleResize.bind(this)

    this.activeIndex = null
    this.requestSequence = 0
    this.lightboxUrls = new Map()
    this.fullsizeUrls = new Map()
    this.fullLoadingPromise = null
    this.previousFocusedElement = null
    this.previousBodyOverflow = ""

    this.resetInteractionState()
  }

  disconnect() {
    this.close()
  }

  open(event) {
    this.activeIndex = Number.parseInt(event.currentTarget.dataset.lightboxIndex, 10)
    this.previousFocusedElement = document.activeElement
    this.previousBodyOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    this.overlayTarget.classList.remove("hidden")
    document.addEventListener("keydown", this.handleKeydown)
    window.addEventListener("resize", this.handleResize)
    this.closeButtonTarget.focus()
    this.showCurrent()
  }

  close() {
    this.overlayTarget.classList.add("hidden")
    document.body.style.overflow = this.previousBodyOverflow
    document.removeEventListener("keydown", this.handleKeydown)
    window.removeEventListener("resize", this.handleResize)
    this.requestSequence += 1
    this.activeIndex = null
    this.imageTarget.removeAttribute("src")
    this.captionTarget.textContent = ""
    this.resetInteractionState()

    if (this.previousFocusedElement?.focus) {
      this.previousFocusedElement.focus()
    }
  }

  previous() {
    if (this.itemTargets.length === 0) {
      return
    }

    if (this.activeIndex === null) {
      this.activeIndex = 0
    }

    this.activeIndex = (this.activeIndex - 1 + this.itemTargets.length) % this.itemTargets.length
    this.showCurrent()
  }

  next() {
    if (this.itemTargets.length === 0) {
      return
    }

    if (this.activeIndex === null) {
      this.activeIndex = 0
    }

    this.activeIndex = (this.activeIndex + 1) % this.itemTargets.length
    this.showCurrent()
  }

  backdropClose(event) {
    if (event.target === this.overlayTarget) {
      this.close()
    }
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  suppressContextMenu(event) {
    event.preventDefault()
  }

  async onStageClick(event) {
    if (event.detail !== 1 || this.dragActive || this.ignoreNextClick) {
      this.ignoreNextClick = false
      return
    }

    if (!this.fullLoaded) {
      await this.loadFullResolutionAndZoom(event)
    } else if (!this.isZoomed) {
      this.zoomIn(event)
    }
  }

  async onStageDoubleClick(event) {
    event.preventDefault()

    if (!this.fullLoaded) {
      await this.loadFullResolutionAndZoom(event)
      return
    }

    if (event.shiftKey) {
      this.zoomOut(event)
    } else {
      this.zoomIn(event)
    }
  }

  async onStageWheel(event) {
    event.preventDefault()

    if (!this.fullLoaded) {
      await this.loadFullResolution(event)
    }

    if (!this.fullLoaded) {
      return
    }

    if (event.deltaY < 0) {
      this.zoomIn(event)
    } else {
      this.zoomOut(event)
    }
  }

  startPan(event) {
    if (event.pointerType === "touch") {
      this.activeTouchPoints.set(event.pointerId, { x: event.clientX, y: event.clientY })

      if (this.activeTouchPoints.size === 2) {
        this.beginPinchGesture()
        return
      }
    }

    if (this.pinchActive) {
      return
    }

    if (!this.isZoomed && event.pointerType === "touch") {
      this.swipeTracking = true
      this.pointerId = event.pointerId
      this.swipeStartX = event.clientX
      this.swipeStartY = event.clientY
      this.swipeDeltaX = 0
      this.swipeDeltaY = 0
      this.swipeLocked = false
      this.swipeDirection = null
      return
    }

    if (!this.isZoomed || !this.canPan || event.pointerType === "mouse" && event.button !== 0) {
      return
    }

    this.dragActive = true
    this.pointerId = event.pointerId
    this.dragStartX = event.clientX
    this.dragStartY = event.clientY
    this.dragOriginX = this.translateX
    this.dragOriginY = this.translateY
    this.stageTarget.setPointerCapture?.(event.pointerId)
    this.syncUi()
    event.preventDefault()
  }

  pan(event) {
    if (this.pinchActive && this.activeTouchPoints.has(event.pointerId)) {
      this.activeTouchPoints.set(event.pointerId, { x: event.clientX, y: event.clientY })
      this.updatePinchGesture()
      event.preventDefault()
      return
    }

    if (this.swipeTracking && event.pointerId === this.pointerId) {
      this.swipeDeltaX = event.clientX - this.swipeStartX
      this.swipeDeltaY = event.clientY - this.swipeStartY

      if (!this.swipeLocked) {
        const absX = Math.abs(this.swipeDeltaX)
        const absY = Math.abs(this.swipeDeltaY)

        if (Math.max(absX, absY) >= SWIPE_DIRECTION_LOCK_PX) {
          this.swipeLocked = true
          this.swipeDirection = absX > absY ? "horizontal" : "vertical"
        }
      }

      if (this.swipeDirection === "horizontal") {
        event.preventDefault()
      }

      return
    }

    if (!this.dragActive || event.pointerId !== this.pointerId) {
      return
    }

    this.translateX = this.dragOriginX + (event.clientX - this.dragStartX)
    this.translateY = this.dragOriginY + (event.clientY - this.dragStartY)
    this.clampTranslation()
    this.applyTransform()
    event.preventDefault()
  }

  endPan(event) {
    if (event.pointerType === "touch") {
      this.activeTouchPoints.delete(event.pointerId)

      if (this.pinchActive) {
        this.endPinchGesture()
        this.pointerId = null
        this.ignoreNextClick = true
        return
      }
    }

    if (this.swipeTracking && event.pointerId === this.pointerId) {
      const handledSwipe = this.completeSwipeGesture()
      this.pointerId = null

      if (handledSwipe) {
        this.ignoreNextClick = true
      }

      return
    }

    if (!this.dragActive || event.pointerId !== this.pointerId) {
      return
    }

    this.dragActive = false
    this.pointerId = null
    this.stageTarget.releasePointerCapture?.(event.pointerId)
    this.syncUi()
  }

  handleResize() {
    if (!this.isOpen) {
      return
    }

    this.recomputeGeometry()
  }

  async handleKeydown(event) {
    if (!this.isOpen) {
      return
    }

    if (event.key === "Tab") {
      this.trapFocus(event)
      return
    }

    if (event.key === "Escape") {
      this.close()
      return
    }

    if (event.key === "ArrowLeft") {
      event.preventDefault()
      this.previous()
      return
    }

    if (event.key === "ArrowRight") {
      event.preventDefault()
      this.next()
      return
    }

    if (event.key === "0") {
      event.preventDefault()
      this.resetZoom()
      return
    }

    if (event.key === "+" || event.key === "=") {
      event.preventDefault()

      if (!this.fullLoaded) {
        await this.loadFullResolution()
      }

      this.zoomIn()
      return
    }

    if (event.key === "-" || event.key === "_") {
      event.preventDefault()
      this.zoomOut()
    }
  }

  async showCurrent() {
    const item = this.currentItem
    if (!item) {
      return
    }

    const requestId = ++this.requestSequence
    this.resetForNewImage()
    this.captionTarget.textContent = item.dataset.caption || ""
    this.imageTarget.alt = item.dataset.caption || ""

    const lightboxUrl = await this.fetchVariantUrl(item.dataset.lightboxFetchUrl, this.lightboxUrls)
    if (!lightboxUrl || requestId !== this.requestSequence) {
      return
    }

    this.currentLightboxSrc = lightboxUrl

    try {
      await this.assignImageSource(lightboxUrl)
      if (requestId !== this.requestSequence) {
        return
      }

      this.recomputeGeometry()
      this.preloadAdjacentLightboxImages()
    } catch (_error) {
      if (requestId !== this.requestSequence) {
        return
      }

      this.imageTarget.removeAttribute("src")
      this.captionTarget.textContent = IMAGE_LOAD_ERROR_MESSAGE
      this.syncUi()
    }
  }

  async loadFullResolutionAndZoom(event = null) {
    const loaded = await this.loadFullResolution(event)
    if (!loaded) {
      return
    }

    this.zoomIn(event)
  }

  async loadFullResolution(event = null) {
    if (this.fullLoaded) {
      return true
    }

    if (this.fullLoading) {
      await this.fullLoadingPromise
      return this.fullLoaded
    }

    const item = this.currentItem
    if (!item) {
      return false
    }

    this.fullLoading = true
    this.syncUi()

    this.fullLoadingPromise = (async () => {
      try {
        const fullUrl = await this.fetchVariantUrl(item.dataset.fullsizeFetchUrl, this.fullsizeUrls)
        if (!fullUrl) {
          return false
        }

        this.currentFullSrc = fullUrl
        await this.preloadImage(fullUrl)

        if (!this.currentItem || this.currentItem !== item) {
          return false
        }

        await this.assignImageSource(fullUrl)
        this.fullLoaded = true
        this.recomputeGeometry()
        return true
      } catch (_error) {
        this.captionTarget.textContent = FULL_LOAD_ERROR_MESSAGE
        return false
      } finally {
        this.fullLoading = false
        this.fullLoadingPromise = null
        this.syncUi()
      }
    })()

    return this.fullLoadingPromise
  }

  zoomIn(event = null) {
    if (!this.fullLoaded) {
      return
    }

    const nextScale = this.nextZoomStepAbove(this.currentScale)
    this.setScale(nextScale, event)
  }

  zoomOut(event = null) {
    const nextScale = this.nextZoomStepBelow(this.currentScale)
    this.setScale(nextScale, event)
  }

  resetZoom() {
    this.currentScale = 1
    this.translateX = 0
    this.translateY = 0
    this.isZoomed = false

    if (this.currentLightboxSrc && this.imageTarget.getAttribute("src") !== this.currentLightboxSrc) {
      this.imageTarget.src = this.currentLightboxSrc
    }

    this.recomputeGeometry()
  }

  setScale(nextScale, event = null) {
    const clampedScale = this.clampScale(nextScale)
    if (Math.abs(clampedScale - this.currentScale) < 0.001) {
      return
    }

    const oldScale = this.currentScale
    const pointer = this.pointerPositionInViewport(event)

    if (pointer) {
      const imageSpaceX = (pointer.x - this.translateX) / oldScale
      const imageSpaceY = (pointer.y - this.translateY) / oldScale

      this.translateX = pointer.x - imageSpaceX * clampedScale
      this.translateY = pointer.y - imageSpaceY * clampedScale
    } else {
      this.translateX *= clampedScale / oldScale
      this.translateY *= clampedScale / oldScale
    }

    this.currentScale = clampedScale
    this.isZoomed = this.currentScale > 1.001
    this.clampTranslation()
    this.applyTransform()
    this.syncUi()
  }

  recomputeGeometry() {
    const { width, height } = this.currentDimensions
    this.viewportWidth = this.viewportTarget.clientWidth || window.innerWidth || width
    this.viewportHeight = this.viewportTarget.clientHeight || window.innerHeight || height

    const fitSize = this.fitWithinViewport(width, height, this.viewportWidth, this.viewportHeight)
    this.fitWidth = fitSize.width
    this.fitHeight = fitSize.height
    this.maxScale = this.computeMaxScale(width, height, fitSize.width, fitSize.height)
    this.zoomSteps = this.buildZoomSteps(this.maxScale)
    this.currentScale = this.clampScale(this.currentScale)
    this.isZoomed = this.currentScale > 1.001

    this.stageTarget.style.width = `${fitSize.width}px`
    this.stageTarget.style.height = `${fitSize.height}px`
    this.clampTranslation()
    this.applyTransform()
    this.syncUi()
  }

  applyTransform() {
    this.stageTarget.style.transform = `translate3d(${this.translateX}px, ${this.translateY}px, 0) scale(${this.currentScale})`
  }

  clampTranslation() {
    const zoomedWidth = this.fitWidth * this.currentScale
    const zoomedHeight = this.fitHeight * this.currentScale

    const maxX = Math.max(0, (zoomedWidth - this.viewportWidth) / 2)
    const maxY = Math.max(0, (zoomedHeight - this.viewportHeight) / 2)

    this.translateX = Math.min(maxX, Math.max(-maxX, this.translateX))
    this.translateY = Math.min(maxY, Math.max(-maxY, this.translateY))
  }

  clampScale(scale) {
    return Math.min(this.maxScale, Math.max(1, scale))
  }

  computeMaxScale(naturalWidth, naturalHeight, fitWidth, fitHeight) {
    const maxScaleByWidth = naturalWidth / fitWidth
    const maxScaleByHeight = naturalHeight / fitHeight
    const maxUsefulScale = Math.min(maxScaleByWidth, maxScaleByHeight)

    return Math.max(1, Math.min(CONFIGURED_ZOOM_STEPS.at(-1), maxUsefulScale))
  }

  buildZoomSteps(maxScale) {
    const steps = CONFIGURED_ZOOM_STEPS.filter((step) => step < maxScale)

    if (steps[0] !== 1) {
      steps.unshift(1)
    }

    if (steps.at(-1) !== maxScale) {
      steps.push(maxScale)
    }

    return steps
  }

  nextZoomStepAbove(scale) {
    return this.zoomSteps.find((step) => step > scale + 0.001) || this.zoomSteps.at(-1) || 1
  }

  nextZoomStepBelow(scale) {
    const descending = [...this.zoomSteps].reverse()
    return descending.find((step) => step < scale - 0.001) || 1
  }

  pointerPositionInViewport(event) {
    if (typeof event?.clientX !== "number" || typeof event?.clientY !== "number") {
      return null
    }

    const rect = this.viewportTarget.getBoundingClientRect()
    return {
      x: event.clientX - rect.left - rect.width / 2,
      y: event.clientY - rect.top - rect.height / 2
    }
  }

  fitWithinViewport(width, height, viewportWidth, viewportHeight) {
    const scale = Math.min(viewportWidth / width, viewportHeight / height)

    return {
      width: Math.max(1, Math.round(width * scale)),
      height: Math.max(1, Math.round(height * scale))
    }
  }

  get currentDimensions() {
    const item = this.currentItem
    const width = Number.parseInt(item?.dataset.intrinsicWidth || "0", 10)
    const height = Number.parseInt(item?.dataset.intrinsicHeight || "0", 10)

    if (width > 0 && height > 0) {
      return { width, height }
    }

    if (this.imageTarget.naturalWidth > 0 && this.imageTarget.naturalHeight > 0) {
      return {
        width: this.imageTarget.naturalWidth,
        height: this.imageTarget.naturalHeight
      }
    }

    return { width: 4, height: 3 }
  }

  async fetchVariantUrl(fetchUrl, cache) {
    if (!fetchUrl) {
      return null
    }

    if (cache.has(fetchUrl)) {
      return cache.get(fetchUrl)
    }

    const response = await fetch(fetchUrl, { headers: { Accept: "application/json" } })
    if (!response.ok) {
      throw new Error(`Image url request failed with status ${response.status}`)
    }

    const payload = await response.json()
    cache.set(fetchUrl, payload.url)
    return payload.url
  }

  async assignImageSource(src) {
    return new Promise((resolve, reject) => {
      const handleLoad = () => {
        cleanup()
        resolve()
      }

      const handleError = () => {
        cleanup()
        reject(new Error("Image failed to load"))
      }

      const cleanup = () => {
        this.imageTarget.removeEventListener("load", handleLoad)
        this.imageTarget.removeEventListener("error", handleError)
      }

      this.imageTarget.addEventListener("load", handleLoad)
      this.imageTarget.addEventListener("error", handleError)
      this.imageTarget.src = src

      if (this.imageTarget.complete && this.imageTarget.naturalWidth > 0) {
        cleanup()
        resolve()
      }
    })
  }

  async preloadImage(src) {
    return new Promise((resolve, reject) => {
      const image = new Image()
      image.onload = () => resolve()
      image.onerror = () => reject(new Error("Preload failed"))
      image.src = src
    })
  }

  preloadAdjacentLightboxImages() {
    for (const item of this.adjacentItems) {
      const fetchUrl = item.dataset.lightboxFetchUrl
      if (!fetchUrl) {
        continue
      }

      this.fetchVariantUrl(fetchUrl, this.lightboxUrls)
        .then((url) => this.preloadImage(url))
        .catch(() => {})
    }
  }

  get adjacentItems() {
    if (this.itemTargets.length < 2 || this.activeIndex === null) {
      return []
    }

    const previousIndex = (this.activeIndex - 1 + this.itemTargets.length) % this.itemTargets.length
    const nextIndex = (this.activeIndex + 1) % this.itemTargets.length
    return [this.itemTargets[previousIndex], this.itemTargets[nextIndex]]
  }

  trapFocus(event) {
    const focusables = [
      this.closeButtonTarget,
      this.previousButtonTarget,
      this.nextButtonTarget
    ].filter((element) => element && !element.classList.contains("hidden") && !element.disabled)

    if (focusables.length === 0) {
      event.preventDefault()
      return
    }

    const currentIndex = focusables.indexOf(document.activeElement)
    const nextIndex = event.shiftKey
      ? (currentIndex <= 0 ? focusables.length - 1 : currentIndex - 1)
      : (currentIndex === -1 || currentIndex === focusables.length - 1 ? 0 : currentIndex + 1)

    event.preventDefault()
    focusables[nextIndex].focus()
  }

  syncUi() {
    const interactive = Boolean(this.imageTarget.getAttribute("src"))
    const canZoomIn = this.currentScale < this.maxScale - 0.001

    this.captionTarget.classList.toggle("hidden", this.isZoomed)
    this.loadingTarget.classList.toggle("hidden", !this.fullLoading)
    this.loadingTarget.classList.toggle("flex", this.fullLoading)
    this.stageTarget.classList.toggle("cursor-zoom-in", interactive && !this.isZoomed)
    this.stageTarget.classList.toggle("cursor-grab", interactive && this.isZoomed && this.canPan && !this.dragActive)
    this.stageTarget.classList.toggle("cursor-grabbing", interactive && this.dragActive)
    this.stageTarget.classList.toggle("cursor-default", interactive && this.isZoomed && !this.canPan && !canZoomIn)
  }

  resetForNewImage() {
    this.resetInteractionState()
    this.currentLightboxSrc = null
    this.currentFullSrc = null
    this.syncUi()
  }

  resetInteractionState() {
    this.currentScale = 1
    this.maxScale = 1
    this.translateX = 0
    this.translateY = 0
    this.fitWidth = 0
    this.fitHeight = 0
    this.viewportWidth = 0
    this.viewportHeight = 0
    this.zoomSteps = [1]
    this.fullLoaded = false
    this.fullLoading = false
    this.pinchActive = false
    this.activeTouchPoints = new Map()
    this.pinchStartDistance = 0
    this.pinchStartScale = 1
    this.pinchCenter = null
    this.swipeTracking = false
    this.swipeStartX = 0
    this.swipeStartY = 0
    this.swipeDeltaX = 0
    this.swipeDeltaY = 0
    this.swipeLocked = false
    this.swipeDirection = null
    this.ignoreNextClick = false
    this.isZoomed = false
    this.dragActive = false
    this.dragStartX = 0
    this.dragStartY = 0
    this.dragOriginX = 0
    this.dragOriginY = 0
    this.pointerId = null
    if (this.stageTarget?.style) {
      this.stageTarget.style.transform = ""
      this.stageTarget.style.width = ""
      this.stageTarget.style.height = ""
    }
  }

  beginPinchGesture() {
    if (!this.fullLoaded || this.activeTouchPoints.size < 2) {
      return
    }

    const [firstPoint, secondPoint] = [...this.activeTouchPoints.values()]
    const distance = this.distanceBetweenPoints(firstPoint, secondPoint)
    if (distance < PINCH_DISTANCE_EPSILON) {
      return
    }

    this.swipeTracking = false
    this.swipeLocked = false
    this.swipeDirection = null
    this.dragActive = false
    this.pointerId = null
    this.pinchActive = true
    this.pinchStartDistance = distance
    this.pinchStartScale = this.currentScale
    this.pinchCenter = {
      clientX: (firstPoint.x + secondPoint.x) / 2,
      clientY: (firstPoint.y + secondPoint.y) / 2
    }
    this.syncUi()
  }

  updatePinchGesture() {
    if (!this.pinchActive || this.activeTouchPoints.size < 2) {
      return
    }

    const [firstPoint, secondPoint] = [...this.activeTouchPoints.values()]
    const distance = this.distanceBetweenPoints(firstPoint, secondPoint)
    if (distance < PINCH_DISTANCE_EPSILON || this.pinchStartDistance < PINCH_DISTANCE_EPSILON) {
      return
    }

    const nextScale = this.pinchStartScale * (distance / this.pinchStartDistance)
    const midpoint = {
      clientX: (firstPoint.x + secondPoint.x) / 2,
      clientY: (firstPoint.y + secondPoint.y) / 2
    }

    this.setScale(nextScale, midpoint)
  }

  endPinchGesture() {
    this.pinchActive = false
    this.pinchStartDistance = 0
    this.pinchStartScale = this.currentScale
    this.pinchCenter = null
    this.syncUi()
  }

  completeSwipeGesture() {
    const handledSwipe = this.swipeDirection === "horizontal" && Math.abs(this.swipeDeltaX) >= SWIPE_THRESHOLD_PX
    const swipeDeltaX = this.swipeDeltaX

    this.swipeTracking = false
    this.swipeStartX = 0
    this.swipeStartY = 0
    this.swipeDeltaX = 0
    this.swipeDeltaY = 0
    this.swipeLocked = false
    this.swipeDirection = null

    if (!handledSwipe) {
      return false
    }

    if (swipeDeltaX < 0) {
      this.next()
    } else {
      this.previous()
    }

    return true
  }

  distanceBetweenPoints(firstPoint, secondPoint) {
    return Math.hypot(secondPoint.x - firstPoint.x, secondPoint.y - firstPoint.y)
  }

  get isOpen() {
    return this.activeIndex !== null
  }

  get canPan() {
    return this.fitWidth * this.currentScale > this.viewportWidth + 1 || this.fitHeight * this.currentScale > this.viewportHeight + 1
  }

  get currentItem() {
    return this.itemTargets[this.activeIndex]
  }
}
